import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  textEvaluationDetails,
  TextEvaluationType,
} from '../../../constants/MemoryGames/textEvaluation';
import { SVGIconType } from '../../../constants/svgIcon';
import { isSpaceBounded, Locale } from '../../../models/Base';
import { EvaluationType } from '../../../models/GameControllerStore';
import {
  InAppEvaluation,
  TextEvaluationWord,
} from '../../../models/RecordStore';
import { SvgIcon, Switch, TTSPlayer } from '../../Molecules';

import styles from './AnalysisTextEvaluationPanel.module.css';

type AnalysisTextEvaluationPanelProps = {
  recordScore?: number;
  recordDuration?: string;
  evaluation: InAppEvaluation<TextEvaluationWord>;
  evaluationKey: EvaluationType;
  answerLocale: Locale;
  isUseTranslate: boolean;
  originalLanguage: string;
  translatedLanguage: string;
  originalAudioTts: string | null | undefined;
  translatedAudioTts: string | null | undefined;
  translationResult: string | undefined;
  isGameEnd: boolean;
  isHandwriting: boolean;
  onSwitchTranslate: () => void;
  onClickLanguage: () => void;
  onClickAnalysis?: () => void;
};

export const AnalysisTextEvaluationPanel: React.FC<
  AnalysisTextEvaluationPanelProps
> = ({
  recordScore,
  recordDuration,
  evaluation,
  evaluationKey,
  answerLocale,
  isUseTranslate,
  originalLanguage,
  translatedLanguage,
  originalAudioTts,
  translatedAudioTts,
  translationResult,
  isGameEnd,
  isHandwriting,
  onSwitchTranslate,
  onClickLanguage,
  onClickAnalysis,
}) => {
  const { t } = useTranslation();

  // Group records into containers based on the break condition
  const renderAnswer = (locale: Locale, words?: TextEvaluationWord[]) => {
    if (!words) return [];

    const paragraphs: JSX.Element[] = [];
    let currentParagraph: JSX.Element[] = [];
    let blankCount = 0;
    let redCount = 0;
    let orangeCount = 0;

    const combineBlanks = () => {
      if (blankCount > 0) {
        const blanks = currentParagraph.splice(-blankCount, blankCount);

        let backgroundColor = '#00A69826';
        if (redCount > 0 || orangeCount > 1) {
          backgroundColor = '#FF595933';
        } else if (orangeCount > 0) {
          backgroundColor = '#F6921E26';
        }

        currentParagraph.push(
          <div
            key={blanks[blanks.length - 1]?.key}
            className={`${styles.answeredWord} ${
              isHandwriting ? styles.handwritingansweredWord : ''
            }`}
            style={{ backgroundColor }}
          >
            {blanks}
          </div>,
        );

        // reset
        blankCount = 0;
        redCount = 0;
        orangeCount = 0;
      }
    };

    const makeParagraph = () => {
      if (currentParagraph.length > 0) {
        combineBlanks();

        // Push the current container into the containers array
        paragraphs.push(
          <div
            key={`container-${paragraphs.length}`}
            className={`${styles.wordContainer} ${
              isGameEnd ? styles.gameEndWordContainer : ''
            }`}
          >
            {currentParagraph}
          </div>,
        );

        // Reset current container
        currentParagraph = [];
      }
    };

    words.forEach((word, wordIndex) => {
      if (word.word === '\n') {
        makeParagraph();

        paragraphs.push(
          <div
            key={`linebreak-${paragraphs.length}`}
            style={{
              // height: '1rem', // Adjust line height as needed
              width: '100%',
              backgroundColor: 'transparent', // Transparent to avoid background conflict
              whiteSpace: 'pre-line',
            }}
          >
            {word.word}
          </div>,
        );
      } else {
        word.units.forEach((unit, unitIndex) => {
          if (unit.unit === '\n' && !unit.isBlank) {
            makeParagraph();
          } else {
            if (unit.score === undefined) {
              combineBlanks();

              currentParagraph.push(
                <span
                  key={`${wordIndex}-${unitIndex}`}
                  style={{
                    maxWidth: '100%',
                    color:
                      textEvaluationDetails[TextEvaluationType.NoData].color,
                  }}
                >
                  {unit.unit}
                </span>,
              );
            } else {
              currentParagraph.push(
                <span
                  key={`${wordIndex}-${unitIndex}`}
                  style={{
                    maxWidth: '100%',
                    color: 'black',
                  }}
                >
                  {unit.unit}
                </span>,
              );

              blankCount += 1;
              redCount += unit.score <= 50 ? 1 : 0;
              orangeCount += unit.score <= 95 ? 1 : 0;

              if (!isSpaceBounded(locale)) {
                combineBlanks();
              }
            }
          }
        });
      }
    });

    makeParagraph();

    return paragraphs;
  };

  return (
    <div
      className={`${styles.container} ${
        isGameEnd ? styles.gameEndContainer : ''
      }`}
      onClick={isGameEnd ? onClickAnalysis : undefined}
    >
      <div
        className={`${styles.recordContainer} ${
          isGameEnd ? '' : styles.whiteBackground
        }`}
      >
        <div className={styles.titleContainer}>
          <SvgIcon
            name={SVGIconType.ScoreAnalysis}
            width={24}
            height={24}
            color={isGameEnd ? 'white' : 'black'}
          />

          <p className={styles.title}>{t('Analysis')}</p>

          {isGameEnd && (
            <SvgIcon
              name={SVGIconType.Arrow}
              width={24}
              height={24}
              color="white"
            />
          )}
        </div>
        <div className={styles.recordRow}>
          <p className={styles.recordLabel}>{t('Record: ')}</p>
          <SvgIcon
            name={SVGIconType.Score}
            width={24}
            height={24}
            color={isGameEnd ? 'white' : 'black'}
          />
          <span>
            {recordScore !== undefined ? Math.floor(recordScore) : '---'}
          </span>
          <SvgIcon
            name={SVGIconType.Timer}
            width={24}
            height={24}
            color={isGameEnd ? 'white' : 'black'}
          />
          <span>{recordDuration ?? '---'}</span>
        </div>
      </div>
      <div className={styles.googleTranslate}>
        <div className={styles.googleTranslateRow}>
          <SvgIcon
            name={SVGIconType.Translation}
            width={24}
            height={24}
            color="white"
          />
          <p className={styles.googleTranslateLabel}>{t('Translation')}</p>
          <Switch
            checked={isUseTranslate}
            label={isUseTranslate ? 'ON' : 'OFF'}
            onChange={isGameEnd ? () => {} : onSwitchTranslate}
          />
        </div>

        <div className={styles.divider} />

        {isUseTranslate ? (
          <div className={styles.languageSelectRow}>
            <p className={styles.languageFromName}>{t(originalLanguage)}</p>
            <SvgIcon
              name={SVGIconType.DirectArrow}
              width={24}
              height={24}
              color="white"
            />
            <div
              className={styles.languageToName}
              onClick={isGameEnd ? () => {} : onClickLanguage}
            >
              <p>{t(translatedLanguage)}</p>
              <SvgIcon
                name={SVGIconType.Triangle}
                color="white"
                width={20}
                height={20}
              />
            </div>
          </div>
        ) : null}
      </div>

      <div className={styles.textContentContainer}>
        <div className={styles.textContentItem}>
          {originalAudioTts ? (
            <div className={styles.ttsPlayer}>
              <TTSPlayer audioSrc={originalAudioTts} />
            </div>
          ) : null}

          <div
            className={`${styles.answeredWordContainer} ${
              isGameEnd ? styles.gameEndWordContainer : ''
            }`}
          >
            {renderAnswer(answerLocale, evaluation?.[evaluationKey])}
          </div>
        </div>

        {isUseTranslate && translationResult ? (
          <>
            <div className={styles.textContentDivider} />
            <div className={styles.textContentItem}>
              {translatedAudioTts ? (
                <div className={styles.ttsPlayer}>
                  <TTSPlayer audioSrc={translatedAudioTts} />
                </div>
              ) : null}

              <p
                className={`${styles.textContent} ${
                  isGameEnd ? styles.gameEndWordContainer : ''
                }`}
              >
                {translationResult}
              </p>
            </div>
          </>
        ) : null}
      </div>

      {onClickAnalysis ? (
        // <div className={styles.seeAllContainer} onClick={onClickAnalysis}>
        //   <div className={styles.allButton}>
        //     <span>{t('All')}</span>
        //     <SvgIcon
        //       name={SVGIconType.Arrow}
        //       color="#808080"
        //       width={14}
        //       height={14}
        //     />
        //   </div>
        // </div>
        <></>
      ) : (
        <div className={styles.textEvaluationColors}>
          {Object.entries(textEvaluationDetails).map(([key, details]) => (
            <div key={key} className={styles.colorItem}>
              <span
                className={styles.colorCircle}
                style={{ backgroundColor: details.color }}
              />
              <span
                className={styles.colorLabel}
                style={{ color: details.color }}
              >
                {t(details.displayLabel)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
